/**
 * Success_Handler.ts
 *
 * Single source of truth for all success toast messages in SUKI.
 * Always shows the REAL backend success message — never hardcoded strings.
 *
 * Backend success response shapes handled:
 *   { message: string }              — most APIs (auth, billing, config, KB)
 *   { data: { message: string } }    — wrapped envelope responses
 *
 * Usage:
 *   import { showSuccess, getSuccessMessage } from './successHandler';
 *
 *   showSuccess(res, 'Login successful');               // shows backend message as subtitle
 *   showSuccess(res, 'Saved', 'Fallback if no message');
 *   getSuccessMessage(res, 'Fallback');                 // pure extractor, no toast
 */

import { showToast } from '../components/toastComponent';

const DEFAULT_FALLBACK = 'Operation completed successfully.';

/* ─────────────────────────────────────────
   Core extractor — mirrors getApiErrorMessage
   Priority:
     1. response.message        (most APIs)
     2. response.data.message   (wrapped envelope)
     3. fallback
───────────────────────────────────────── */

export function getSuccessMessage(response: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!response || typeof response !== 'object') return fallback;

  const res = response as any;

  if (typeof res.message === 'string' && res.message.trim()) {
    return res.message.trim();
  }

  if (typeof res.data?.message === 'string' && res.data.message.trim()) {
    return res.data.message.trim();
  }

  return fallback;
}

/* ─────────────────────────────────────────
   Core displayer
   title   — toast heading (e.g. 'Saved', 'Login successful')
   response — raw backend response object
   fallback — shown as subtitle when backend sends no message
───────────────────────────────────────── */

export function showSuccess(response: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  const message = getSuccessMessage(response, fallback);
  showToast('success', title, message);
}
